/**
 * Felt layout in cell units, and pure hit resolution.
 *
 * One cell of the number grid is 1×1. The grid runs x 0→12, y 0→3, with the
 * zero head column to its left and the outside bets below and to the right —
 * the standard landscape felt.
 *
 *        ┌──────────────────────────────────────┬─────┐
 *        │  3  6  9 12 15 18 21 24 27 30 33 36  │ 2:1 │
 *   0/00 │  2  5  8 11 14 17 20 23 26 29 32 35  │ 2:1 │
 *        │  1  4  7 10 13 16 19 22 25 28 31 34  │ 2:1 │
 *        ├──────────┬───────────┬───────────────┴─────┘
 *        │  1st 12  │  2nd 12   │  3rd 12  │
 *        ├────┬─────┼─────┬─────┼─────┬────┤
 *        │1-18│EVEN │ RED │BLACK│ ODD │19-36
 *
 * Bets that sit on a line rather than in a box — splits, streets, corners, six
 * lines, and the zero-adjacent bets — are *point* targets. Hit resolution
 * snaps to the nearest point within a threshold and otherwise falls back to
 * the box under the cursor, which is what SPEC.md §4 means by "generous hit
 * targets, not exact line hits".
 */

import type { BetType, PocketId, PlacedBet, WheelStyle } from '~/domain/types';
import { encodeTarget, legalPocketSets } from '~/domain/bets';
import { basketPockets, feltCellOf, feltNumber, zeroCells } from '~/domain/felt';

export const GRID_COLS = 12;
export const GRID_ROWS = 3;
export const HEAD_WIDTH = 1;
/** Strip between the number grid and the dozens, where street bets sit. */
export const GUTTER = 0.25;
export const OUTSIDE_ROW_HEIGHT = 0.7;

const DOZEN_Y = GRID_ROWS + GUTTER;
const EVEN_Y = DOZEN_Y + OUTSIDE_ROW_HEIGHT;

/** How far from a line a click still counts as that line's bet, in cell units. */
export const POINT_SNAP = 0.24;

export interface Point {
  readonly x: number;
  readonly y: number;
}

export type RegionKind = 'number' | 'zero' | 'column' | 'dozen' | 'even-money';

/** A box you click to place a bet — straights and the outside bets. */
export interface FeltRegion {
  readonly id: string;
  readonly kind: RegionKind;
  readonly betType: BetType;
  readonly target: string;
  readonly label: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** A point on a line you click to place a bet — everything between boxes. */
export interface FeltPoint {
  readonly id: string;
  readonly betType: BetType;
  readonly target: string;
  readonly label: string;
  readonly x: number;
  readonly y: number;
}

export interface FeltLayout {
  readonly style: WheelStyle;
  readonly minX: number;
  readonly minY: number;
  readonly width: number;
  readonly height: number;
  readonly regions: readonly FeltRegion[];
  readonly points: readonly FeltPoint[];
  /** Region holding each pocket's straight bet, for highlighting. */
  readonly pocketRegions: ReadonlyMap<PocketId, FeltRegion>;
}

export type FeltHit =
  | { readonly kind: 'point'; readonly point: FeltPoint }
  | { readonly kind: 'region'; readonly region: FeltRegion };

// ---------------------------------------------------------------------------
// Zero-adjacent bets
// ---------------------------------------------------------------------------

/**
 * Where each zero bet sits, keyed by `betType:target`. These cannot be derived
 * from grid adjacency because the head column does not share the grid's rows —
 * on the double-zero felt `00` covers the top half and `0` the bottom half.
 */
function zeroPoints(style: WheelStyle): Record<string, Point> {
  if (style === 'single-zero') {
    return {
      // 0 spans all three rows, so it meets each of 1, 2 and 3 along x = 0.
      'split:0|3': { x: 0, y: 0.5 },
      'split:0|2': { x: 0, y: 1.5 },
      'split:0|1': { x: 0, y: 2.5 },
      'street:0|2|3': { x: 0, y: 1 },
      'street:0|1|2': { x: 0, y: 2 },
      // The "first four" goes on the outer corner where 0 meets 3.
      'corner:0|1|2|3': { x: 0, y: 0 },
    };
  }
  return {
    'split:0|00': { x: -HEAD_WIDTH / 2, y: 1.5 },
    'split:00|3': { x: 0, y: 0.75 },
    'split:00|2': { x: 0, y: 1.25 },
    'split:0|2': { x: 0, y: 1.75 },
    'split:0|1': { x: 0, y: 2.5 },
    'street:00|2|3': { x: 0, y: 1 },
    'street:0|1|2': { x: 0, y: 2 },
    // The five-number bet sits on the outer corner, like the first four.
    'basket:0|00|1|2|3': { x: 0, y: 0 },
  };
}

// ---------------------------------------------------------------------------
// Positions for bets among 1–36
// ---------------------------------------------------------------------------

function numericPoint(betType: BetType, pockets: readonly PocketId[]): Point | null {
  const cells = pockets.map((pocketId) => feltCellOf(Number(pocketId)));
  const cols = cells.map((cell) => cell.col);
  const rows = cells.map((cell) => cell.row);
  const minCol = Math.min(...cols);
  const minRow = Math.min(...rows);

  switch (betType) {
    case 'split': {
      const [a, b] = cells;
      if (!a || !b) return null;
      // Neighbours across a column boundary, or down a column.
      return a.row === b.row
        ? { x: minCol + 1, y: a.row + 0.5 }
        : { x: a.col + 0.5, y: minRow + 1 };
    }
    case 'street':
      // On the edge line below the street's column.
      return { x: minCol + 0.5, y: GRID_ROWS };
    case 'corner':
      return { x: minCol + 1, y: minRow + 1 };
    case 'six-line':
      return { x: minCol + 1, y: GRID_ROWS };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Layout construction
// ---------------------------------------------------------------------------

const EVEN_MONEY_ORDER: readonly { betType: BetType; label: string }[] = [
  { betType: 'low', label: '1–18' },
  { betType: 'even', label: 'EVEN' },
  { betType: 'red', label: 'RED' },
  { betType: 'black', label: 'BLACK' },
  { betType: 'odd', label: 'ODD' },
  { betType: 'high', label: '19–36' },
];

function buildRegions(style: WheelStyle): FeltRegion[] {
  const regions: FeltRegion[] = [];

  for (const zero of zeroCells(style)) {
    regions.push({
      id: `zero:${zero.id}`,
      kind: 'zero',
      betType: 'straight',
      target: zero.id,
      label: zero.id,
      x: -HEAD_WIDTH,
      y: zero.rowStart,
      w: HEAD_WIDTH,
      h: zero.rowEnd - zero.rowStart,
    });
  }

  for (let col = 0; col < GRID_COLS; col += 1) {
    for (let row = 0; row < GRID_ROWS; row += 1) {
      const value = feltNumber(col, row);
      regions.push({
        id: `number:${value}`,
        kind: 'number',
        betType: 'straight',
        target: String(value),
        label: String(value),
        x: col,
        y: row,
        w: 1,
        h: 1,
      });
    }
  }

  // Column bets sit at the far end of their row. Row 0 holds 3, 6, 9… so it is
  // column 3.
  for (let row = 0; row < GRID_ROWS; row += 1) {
    const column = GRID_ROWS - row;
    regions.push({
      id: `column:${column}`,
      kind: 'column',
      betType: 'column',
      target: String(column),
      label: '2:1',
      x: GRID_COLS,
      y: row,
      w: 1,
      h: 1,
    });
  }

  const dozenLabels = ['1st 12', '2nd 12', '3rd 12'];
  for (let dozen = 1; dozen <= 3; dozen += 1) {
    regions.push({
      id: `dozen:${dozen}`,
      kind: 'dozen',
      betType: 'dozen',
      target: String(dozen),
      label: dozenLabels[dozen - 1] ?? `${dozen}`,
      x: (dozen - 1) * 4,
      y: DOZEN_Y,
      w: 4,
      h: OUTSIDE_ROW_HEIGHT,
    });
  }

  EVEN_MONEY_ORDER.forEach((entry, index) => {
    regions.push({
      id: `even:${entry.betType}`,
      kind: 'even-money',
      betType: entry.betType,
      target: '',
      label: entry.label,
      x: index * 2,
      y: EVEN_Y,
      w: 2,
      h: OUTSIDE_ROW_HEIGHT,
    });
  });

  return regions;
}

function buildPoints(style: WheelStyle): FeltPoint[] {
  const points: FeltPoint[] = [];
  const zeros = zeroPoints(style);
  const lineBets: BetType[] = ['split', 'street', 'corner', 'six-line'];

  const push = (betType: BetType, pockets: readonly PocketId[], at: Point) => {
    const target = encodeTarget(pockets);
    points.push({
      id: `${betType}:${target}`,
      betType,
      target,
      label: `${betType} ${pockets.join('/')}`,
      x: at.x,
      y: at.y,
    });
  };

  for (const betType of lineBets) {
    for (const pockets of legalPocketSets(betType, style)) {
      const key = `${betType}:${encodeTarget(pockets)}`;
      const zeroAt = zeros[key];
      if (zeroAt) {
        push(betType, pockets, zeroAt);
        continue;
      }
      const at = numericPoint(betType, pockets);
      if (at) push(betType, pockets, at);
    }
  }

  const basket = basketPockets(style);
  if (basket) {
    const at = zeros[`basket:${encodeTarget(basket)}`];
    if (at) push('basket', basket, at);
  }

  return points;
}

const layoutCache = new Map<WheelStyle, FeltLayout>();

export function feltLayout(style: WheelStyle): FeltLayout {
  const cached = layoutCache.get(style);
  if (cached) return cached;

  const regions = buildRegions(style);
  const points = buildPoints(style);
  const pocketRegions = new Map<PocketId, FeltRegion>();
  for (const region of regions) {
    if (region.kind === 'number' || region.kind === 'zero') {
      pocketRegions.set(region.target as PocketId, region);
    }
  }

  const layout: FeltLayout = {
    style,
    minX: -HEAD_WIDTH,
    minY: 0,
    width: HEAD_WIDTH + GRID_COLS + 1,
    height: EVEN_Y + OUTSIDE_ROW_HEIGHT,
    regions,
    points,
    pocketRegions,
  };
  layoutCache.set(style, layout);
  return layout;
}

// ---------------------------------------------------------------------------
// Hit resolution
// ---------------------------------------------------------------------------

function contains(region: FeltRegion, x: number, y: number): boolean {
  return x >= region.x && x <= region.x + region.w && y >= region.y && y <= region.y + region.h;
}

/**
 * What a click at (x, y) in cell units means.
 *
 * Nearest line-bet point wins if it is within `POINT_SNAP`; otherwise the box
 * under the cursor does. That leaves roughly the middle half of a number cell
 * as a straight bet and the bands around its edges as splits, with the grid
 * vertices resolving to corners.
 */
export function resolveFeltHit(layout: FeltLayout, x: number, y: number): FeltHit | null {
  let nearest: FeltPoint | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (const point of layout.points) {
    const distance = Math.hypot(point.x - x, point.y - y);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = point;
    }
  }

  if (nearest && nearestDistance <= POINT_SNAP) return { kind: 'point', point: nearest };

  for (const region of layout.regions) {
    if (contains(region, x, y)) return { kind: 'region', region };
  }
  return null;
}

/** Where a placed bet's chip is drawn. */
export function betPosition(layout: FeltLayout, bet: Pick<PlacedBet, 'betType' | 'target'>): Point | null {
  const key = `${bet.betType}:${bet.target}`;
  const point = layout.points.find((candidate) => candidate.id === key);
  if (point) return { x: point.x, y: point.y };

  const region = layout.regions.find(
    (candidate) => candidate.betType === bet.betType && candidate.target === bet.target,
  );
  if (region) return { x: region.x + region.w / 2, y: region.y + region.h / 2 };
  return null;
}
